import type { Cursor } from "./cursor";
import type { ImageOverlay } from "./image-overlay";
import type { LazyVideoManager } from "./lazy-video";
import type { VideoOverlay } from "./video-overlay";
import type { NativeScroller } from "kido/native-scroller";
import type { Scroller } from "kido/scroller";

export type RouteUrl = string;
export type RoutePageKey = string | null;

export interface RouteSnapshot {
  url: RouteUrl | null;
  page: RoutePageKey;
}

export interface RouteState {
  old: RouteSnapshot;
  new: RouteSnapshot;
}

export interface RouteCacheEntry {
  title?: string;
  html?: string;
}

export interface TextureLayer {
  id?: string;
  url: string;
  type?: "image" | "video";
  [key: string]: unknown;
}

/** Texture store: element name -> array of texture layer arrays */
export type TextureStore = Record<string, TextureLayer[][]>;

/** Route texture configuration */
export interface RouteTextureConfig {
  textures: TextureStore;
}

/** Route selector configuration: element name -> array of CSS selectors */
export type RouteSelectorConfig = Record<
  string,
  Array<string | null> | undefined
>;

export interface RouteMediaSlot {
  attrib: Array<GPUTexture | null>;
  sampler: GPUSampler | null;
  data: Array<{ v: boolean; element: HTMLImageElement | null }>;
  layerL: number;
  wh: number;
  id?: string | undefined;
  type?: "image" | "video";
}

export type RouteMediaElementStore = Record<string, RouteMediaSlot[]>;
export type RouteMediaStore = Record<RouteUrl, RouteMediaElementStore>;

export interface GpuRendererLike {
  needsRedraw: boolean;
  extraFrames: number;
  requestBoundsUpdate: () => void;
  requestRedraw?: () => void;
  resize?: () => void;
  setBoundsUpdateMode?: (mode: "always" | "throttled") => void;
  boundsUpdate?: {
    mode: "always" | "throttled";
    interval: number;
    force: boolean;
    last: number;
  };
}

export interface GpuSceneLike {
  resetHoverEffects?(): unknown;
  url?: string | null;
  planes?: unknown[];
  domSlots?: unknown[];
  collectDomSlots?: () => void;
  updateBounds?: () => void;
  reinitVideoPlanes?: () => void;
  syncTexturesFromSlots?: () => boolean;
  injectProjectMedia?: (projectRouteUrl: string) => void;
  restorePlanes?: () => void;
  buildPlaneMap?: (
    id: string,
    mode: "to-case" | "to-home" | "to-project" | "to-projects"
  ) => { map: unknown[]; other: unknown[] } | undefined;
}

export interface GpuTransitionState {
  fromScene: GpuSceneLike | null;
  toScene: GpuSceneLike | null;
  progress?: number;
  mode?: string;
}

export interface PageTransitState {
  coverBounds?: { x: number; y: number; w: number; h: number };
  otherPlaneBounds?: { x: number; y: number; w: number; h: number }[];
  slug: string;
  planeId?: string;
  direction?: "to-project" | "to-projects" | "to-play" | "to-play-detail";
}

export interface GpuInstance {
  device: GPUDevice | null;
  media: RouteMediaStore;
  renderer: GpuRendererLike | null;
  scene: GpuSceneLike | null;
  scenes: Record<string, GpuSceneLike>;
  transition: GpuTransitionState | null;
  isMutating: boolean;
  boundsFrozen: boolean;
  /** When true, a page owns the render loop — main GPU _frame() skips rendering */
  pageOwnsRender: boolean;
  freezeBounds?: (frozen: boolean) => void;
  requestExtraFrames?: () => void;
  requestBoundsUpdate?: () => void;
  requestRedraw?: () => void;
  setPreRender?: (cb: (() => void) | null) => void;
  load?: () => Promise<void>;
  intro?: () => void;
  init?: () => void;
  run?: () => void;
  prepareTransition?: (fromUrl: string, toUrl: string, mode?: string) => void;
  commitTransition?: () => void;
  videosPlaying?: boolean;
  setVideosPlaying?: (playing: boolean) => void;
}

export interface AppState {
  config: { routes: Record<RouteUrl, string> };
  route: RouteState;
  is: Record<string, boolean>;
  was: Record<string, boolean>;
  cache: Record<RouteUrl, RouteCacheEntry>;
  mutating: boolean;
  container: HTMLElement | null;
  app: HTMLElement | null;
  win: { w: number; h: number };
  gpu: GpuInstance | null;
  target: unknown;
  scroller: Scroller | NativeScroller | null;
  sections: any;
  pageManager: any;
  transitState: PageTransitState | null;
  cursor: Cursor | null;
  videoOverlay: VideoOverlay | null;
  imageOverlay: ImageOverlay | null;
  lazyVideo: LazyVideoManager | null;
  introDone: boolean;
  introSwipe: Promise<void>;
  _introSwipeResolve: (() => void) | null;
  revealLocked: boolean;
  data: {
    gpu: {
      cacheMax: number;
      textures: Record<RouteUrl, RouteTextureConfig>;
      selectors: Record<RouteUrl, RouteSelectorConfig>;
    };
  };
}

const context: AppState = {
  config: { routes: Object.create(null) as Record<RouteUrl, string> },
  route: {
    old: { url: null, page: null },
    new: { url: null, page: null },
  },
  is: Object.create(null) as Record<string, boolean>,
  was: Object.create(null) as Record<string, boolean>,
  cache: Object.create(null) as Record<RouteUrl, RouteCacheEntry>,
  mutating: false,
  app: null,
  container: null,
  win: { w: 0, h: 0 },
  gpu: null,
  target: null,
  scroller: null,
  sections: null,
  pageManager: null,
  transitState: null,
  cursor: null,
  videoOverlay: null,
  imageOverlay: null,
  lazyVideo: null,
  introDone: false,
  introSwipe: null! as Promise<void>,
  _introSwipeResolve: null,
  revealLocked: false,
  data: {
    gpu: {
      cacheMax: 3,
      textures: Object.create(null) as Record<RouteUrl, RouteTextureConfig>,
      selectors: Object.create(null) as Record<RouteUrl, RouteSelectorConfig>,
    },
  },
};

// Wire up introSwipe promise
context.introSwipe = new Promise<void>((resolve) => {
  context._introSwipeResolve = resolve;
});

export const App: AppState = context;

// Expose App globally for debugging
if (typeof window !== "undefined") {
  (window as any).App = App;
}
